import { useAuthenticationContext } from "@climatepartner/app-shell/authentication-context";
import { Button } from "@climatepartner/components/Button";
import { Spinner } from "@climatepartner/components/Spinner";
import { Alert, AlertTitle, Stack } from "@mui/material";
import { InvitationScreen } from "@service/network/supplier/dto/resolve-invite-response.dto";
import { ReactElement, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { rumActionNames } from "../../../../../shared/monitoring/rum-action-names";
import { NETWORK_PATH } from "../../../../../shared/routing/paths/network";
import { useAuthenticatedResolve } from "../api/use-authenticated-resolve";
import { toResolveInviteError } from "../api/use-resolve-invite";
import { AccountSelectionCard } from "./account-selection-card";
import { ErrorCard } from "./error-card";
import { toErrorCardVariant } from "./error-card.helpers";
import { ForwardForm } from "./forward-form";
import { InvitationHeaderCard } from "./invitation-header-card";

// Hoisted to constants so the apostrophes don't trip react/no-unescaped-entities as JSX text.
const SUB_ACCOUNT_BLOCK_ERROR_TITLE = "This request needs your company's main account";
const SUB_ACCOUNT_BLOCK_ERROR_BODY =
  "Your account can't respond to climate data requests directly. Only your company's main ClimatePartner account can. Please forward this invitation to your admin there.";

export const AccountSelectionPage = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const cr = searchParams.get("cr") ?? "";
  const token = searchParams.get("token") ?? "";
  const [invitationForwarded, setInvitationForwarded] = useState(false);

  // Re-resolve as the now-authenticated user. This returns the authoritative rootAccounts list, and
  // may flip to CONNECTION_READY if the backend auto-linked the caller to a single root account.
  const { data, isLoading, error, refetch, isFetching } = useAuthenticatedResolve(cr, Boolean(cr));
  const { refreshToken } = useAuthenticationContext().actions;

  const isConnectionReady = data?.screen === InvitationScreen.CONNECTION_READY;
  const hasLeftForNetwork = useRef(false);

  useEffect(() => {
    if (!isConnectionReady || hasLeftForNetwork.current) {
      return;
    }
    hasLeftForNetwork.current = true;

    // The resolve enabled network-supplier-v2 server-side; `hasPermission` reads Frontegg's
    // entitlements store, which predates that grant (AIM-2741).
    //
    // No account-connected toast here: ConnectionReadyScreenDto (single-account auto-link) doesn't
    // return the linked account's name, unlike AccountSelectionCard — see Figma 2118-24858.
    void refreshToken()
      .catch(() => undefined)
      .finally(() => navigate(NETWORK_PATH, { replace: true }));
  }, [isConnectionReady, refreshToken, navigate]);

  // The error and SUB_ACCOUNT_BLOCKED guards must come before the spinner guard below, which swallows
  // every non-selection screen into an infinite spinner.
  if (error) {
    const variant = toErrorCardVariant(toResolveInviteError(error));
    return (
      <Stack sx={{ maxWidth: 560, width: "100%", mx: "auto" }}>
        <ErrorCard
          variant={variant}
          action={
            variant === "network" && (
              <Button
                onClick={() => void refetch()}
                isLoading={isFetching}
                disabled={isFetching}
                data-dd-action-name={rumActionNames.networkSupplierInviteResolveRetried}
              >
                Try again
              </Button>
            )
          }
        />
      </Stack>
    );
  }

  if (data?.screen === InvitationScreen.SUB_ACCOUNT_BLOCKED) {
    return (
      <Stack spacing={4} sx={{ maxWidth: 560, width: "100%", mx: "auto" }}>
        {!invitationForwarded && (
          <Alert severity="error">
            <AlertTitle>{SUB_ACCOUNT_BLOCK_ERROR_TITLE}</AlertTitle>
            {SUB_ACCOUNT_BLOCK_ERROR_BODY}
          </Alert>
        )}
        <ForwardForm
          connectionRequestId={cr}
          token={token}
          heading="Know someone who should handle this?"
          showInvitationHeader={false}
          showFieldsHeading={false}
          onForwarded={() => setInvitationForwarded(true)}
        />
      </Stack>
    );
  }

  // Spinner while resolving, and while the CONNECTION_READY effect performs its redirect.
  if (isLoading || !data || data.screen !== InvitationScreen.ACCOUNT_SELECTION) {
    return <Spinner />;
  }

  const { invitation } = data;

  return (
    <Stack spacing={2} sx={{ maxWidth: 560, width: "100%", mx: "auto" }}>
      <InvitationHeaderCard
        buyerName={invitation.buyerName}
        supplierName={invitation.supplierName}
        respondBy={invitation.respondBy}
      />
      <AccountSelectionCard
        connectionRequestId={cr}
        rootAccounts={data.rootAccounts ?? []}
        buyerName={invitation.buyerName}
      />
    </Stack>
  );
};
